import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';
import { NewsService } from '../../../core/services/news/news.service';
import { News } from '../../../models/news.model';
import { LoadingComponent } from '../../../shared/loading/loading.component';
import { RouterLink } from "@angular/router";

@Component({
  selector: 'app-control-news',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, LoadingComponent, RouterLink],
  templateUrl: './control-news.component.html',
  styleUrls: ['./control-news.component.scss']
})
export class ControlNewsComponent implements OnInit {

  // News Data
  newsList: News[] = [];
  filteredNews: News[] = [];
  loading: boolean = false;
  
  // Form
  newsForm!: FormGroup;
  isEditing = false;
  editId: string | null = null;
  
  // Image
  imagePreview: string | null = null;
  imageFile: File | null = null;
  
  // Messages
  errorMessage: string = '';
  successMessage: string = '';
  
  // Filter
  selectedCategory: string = 'all';
  availableCategories: string[] = [];
  
  // Categories List
  categories = [
    { value: 'سياسة', label: 'سياسة' },
    { value: 'رياضة', label: 'رياضة' },
    { value: 'اقتصاد', label: 'اقتصاد' },
    { value: 'حوادث', label: 'حوادث' },
    { value: 'فن', label: 'فن' },
    { value: 'ثقافة', label: 'ثقافة' },
    { value: 'صحة', label: 'صحة' },
    { value: 'علوم', label: 'علوم' },
    { value: 'تكنولوجيا', label: 'تكنولوجيا' },
    { value: 'تعليم', label: 'تعليم' },
    { value: 'متنوع', label: 'متنوع' },
  ]; 

  constructor(
    private newsService: NewsService,
    private fb: FormBuilder
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.loadNews();
  }

  // ========== INITIALIZATION ==========
  private initializeForm(): void {
    this.newsForm = this.fb.group({
      title: ['', Validators.required],
      article: ['', Validators.required],
      image: [null, Validators.required],
      category: ['', Validators.required],
      editor: [''],
    });
  }

  // ========== LOAD NEWS ==========
  private loadNews(): void {
    this.loading = true;
    this.newsService.getAllNews().subscribe({
      next: (news) => {
        this.newsList = news.reverse();
        this.extractCategories();
        this.applyFilter();
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading news:', error);
        this.errorMessage = 'حدث خطأ أثناء تحميل الأخبار';
        this.loading = false;
      }
    });
  }

  private extractCategories(): void {
    const allCategories = this.newsList
      .map(item => item.category)
      .filter(category => category && category.trim() !== '');
    
    this.availableCategories = [...new Set(allCategories)].sort();
  }

  // ========== FILTER METHODS ==========
  onCategoryFilterChange(): void {
    this.applyFilter();
  }

  private applyFilter(): void {
    if (this.selectedCategory === 'all') {
      this.filteredNews = [...this.newsList];
    } else {
      this.filteredNews = this.newsList.filter(
        item => item.category === this.selectedCategory
      );
    }
  }

  getCategoryCount(category: string): number {
    return this.newsList.filter(item => item.category === category).length;
  }

  // ========== FILE UPLOAD HANDLER ==========
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      
      // Validate file type
      if (!file.type.match(/image\/(jpeg|jpg|png|gif|webp)/)) {
        this.errorMessage = 'يجب أن تكون الصورة من نوع: JPEG, PNG, GIF, أو WebP';
        this.newsForm.get('image')?.setErrors({ invalidFileType: true });
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        this.errorMessage = 'حجم الصورة يجب أن يكون أقل من 5 ميجابايت';
        this.newsForm.get('image')?.setErrors({ fileTooLarge: true });
        return;
      }

      this.imageFile = file;
      this.errorMessage = '';

      // Convert to base64
      const reader = new FileReader();
      reader.onload = () => {
        const base64String = reader.result as string;
        this.imagePreview = base64String;
        this.newsForm.patchValue({
          image: base64String
        });
        this.newsForm.get('image')?.setErrors(null);
      };
      reader.onerror = () => {
        this.errorMessage = 'حدث خطأ أثناء قراءة الصورة';
        this.newsForm.get('image')?.setErrors({ readError: true });
      };
      reader.readAsDataURL(file);
    }
  }

  // ========== REMOVE IMAGE ==========
  removeImage(): void {
    this.imagePreview = null;
    this.imageFile = null;
    this.newsForm.patchValue({ image: null });
    
    // Only set required error if not editing or if editing and no existing image
    if (!this.isEditing || !this.newsForm.get('image')?.value) {
      this.newsForm.get('image')?.setErrors({ required: true });
    }
    
    this.errorMessage = '';
    
    // Reset file input
    const fileInput = document.getElementById('image') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  // ========== SUBMIT (ADD OR UPDATE) ==========
  onSubmit(): void {
    this.clearMessages();

    if (this.newsForm.invalid) {
      this.markFormGroupTouched(this.newsForm);
      this.errorMessage = 'يرجى ملء جميع الحقول المطلوبة';
      return;
    }

    const data: News = {
      title: this.newsForm.value.title,
      article: this.newsForm.value.article,
      image: this.newsForm.value.image,
      category: this.newsForm.value.category,
      editor: this.newsForm.value.editor || '',
      createdAt: Date.now()
    };

    if (!this.isEditing) {
      this.addNews(data);
    } else if (this.editId) {
      this.updateNews(this.editId, data);
    }
  }

  private addNews(data: News): void {
    this.loading = true;
    this.newsService.addNews(data)
      .then(() => {
        this.successMessage = 'تم إضافة الخبر بنجاح';
        this.resetForm();
        this.loadNews();
      })
      .catch((error) => {
        this.handleError(error, 'إضافة');
      })
      .finally(() => {
        this.loading = false;
        this.hideMessageAfterDelay(this.successMessage ? 'success' : 'error');
      });
  }

  private updateNews(id: string, data: News): void {
    this.loading = true;
    this.newsService.updateNews(id, {
      title: data.title,
      article: data.article,
      image: data.image,
      category: data.category,
      editor: data.editor || '',
    })  
      .then(() => {
        this.successMessage = 'تم تحديث الخبر بنجاح';
        this.resetForm();
        this.loadNews();
      })
      .catch((error) => {
        this.handleError(error, 'تحديث');
      })
      .finally(() => {
        this.loading = false;
        this.hideMessageAfterDelay(this.successMessage ? 'success' : 'error');
      });
  }

  private resetForm(): void {
    this.newsForm.reset();
    this.imagePreview = null;
    this.imageFile = null;
    this.isEditing = false;
    this.editId = null;
    this.resetFileInput();
  }

  private resetFileInput(): void {
    const fileInput = document.getElementById('image') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  private clearMessages(): void {
    this.errorMessage = '';
    this.successMessage = '';
  }

  private handleError(error: any, operation: string): void {
    console.error(`Error ${operation} news:`, error);
    if (error.code === 'PERMISSION_DENIED') {
      this.errorMessage = 'خطأ في الصلاحيات: يرجى التحقق من إعدادات قاعدة البيانات في Firebase';
    } else {
      this.errorMessage = `حدث خطأ أثناء ${operation} الخبر: ${error.message || 'خطأ غير معروف'}`;
    }
  }

  private hideMessageAfterDelay(type: 'success' | 'error'): void {
    setTimeout(() => {
      if (type === 'success') {
        this.successMessage = '';
      } else {
        this.errorMessage = '';
      }
    }, 3000);
  }

  // ========== DELETE ==========
  deleteNews(id: string): void {
    if (confirm('هل أنت متأكد من حذف هذا الخبر؟')) {
      this.loading = true;
      this.newsService.deleteNews(id)
        .then(() => {
          this.successMessage = 'تم حذف الخبر بنجاح';
          this.loadNews();
        })
        .catch((error) => {
          this.handleError(error, 'حذف');
          this.loading = false;
        })
        .finally(() => {
          this.hideMessageAfterDelay(this.successMessage ? 'success' : 'error');
        });
    }
  }

  // ========== EDIT ==========
  editNews(item: News) {
    this.isEditing = true;
    this.editId = item.id!;
    this.errorMessage = '';
    this.successMessage = '';

    // Set image preview if it's already a base64 string or URL
    if (item.image) {
      this.imagePreview = item.image;
    }

    this.newsForm.patchValue({
      title: item.title,
      article: item.article,
      image: item.image || null, // Can be base64 or URL
      category: item.category,
      editor: item.editor || '',
    });

    // Clear image validation errors when editing (image already exists)
    if (item.image) {
      this.newsForm.get('image')?.setErrors(null);
    }

    // Scroll to form
    document.querySelector('.form-section')?.scrollIntoView({ behavior: 'smooth' });
  }

  cancelEdit(): void {
    this.resetForm();
    this.clearMessages();
  }
}
